import React, { useState } from 'react';
import { DeleteIcon, EditIcon, EyeIcon } from '../../components/Shared/TableActionIcons';
import { useDispatch } from 'react-redux';
import CustomPagination from '../../components/Shared/CustomPagination';
import { useTranslation } from 'react-i18next';
import TableLoading from '../../components/Shared/TableLoading';
import CustomTable from '../../components/Shared/CustomTable';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/Shared/ConfirmationModal';
import { FiTrash2 } from 'react-icons/fi';
import { deleteAttendance } from '../../store/slices/HrSlice';

export default function AttendanceTable({ data, loading, onPageChange, onRefresh }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedData, setSelectedData] = useState(null);
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [delLoading, setDelLoading] = useState(false);

  const handlePageChange = (newPage, newRowsPerPage) => {
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage);
    }
  };

  const confirmDeleteHandler = async () => {
    try {
      setDelLoading(true);
      if (selectedData) {
        const resultAction = await dispatch(deleteAttendance({ id: selectedData?.id }));
        if (deleteAttendance.fulfilled.match(resultAction)) {
          if (onRefresh) onRefresh();
          setConfirmationDialogVisible(false);
          setSelectedData(null);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDelLoading(false);
    }
  };

  const templateBody = rowData => (
    <div className='flex gap-1'>
      <EyeIcon
        onClick={() => navigate('/manager/attendance/view', { state: { attendance: rowData } })}
        title={t('view')}
      />
      <EditIcon
        onClick={() => navigate('/manager/attendance/create', { state: { editingAttendance: rowData } })}
        title={t('edit')}
      />
      <DeleteIcon
        onClick={() => {
          setSelectedData(rowData);
          setConfirmationDialogVisible(true);
        }}
        title={t('delete')}
      />
    </div>
  );

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (data?.page - 1) * data?.per_page;
  };

  const columnsData = [
    {
      body: templateBody,
      header: t('actions')
    },
    {
      body: sequenceBodyTemplate,
      header: t('no'),
      style: { width: '3rem' }
    },
    {
      field: 'full_name',
      header: t('name'),
      body: rowData => (
        <div>
          {rowData.employee.first_name} {rowData.employee.last_name}
        </div>
      )
    },
    {
      field: 'days',
      header: t('days')
    },
    {
      field: 'month',
      header: t('month')
    },
    {
      field: 'year',
      header: t('year')
    },
  ];

  return (
    <>
      <CustomTable
        data={data?.data}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={data?.per_page}
      />
      <ConfirmationModal
        message={`${t('do_you_really_want_to_delete_this_attendance')}?`}
        header={t('delete_confirmation')}
        Icon={FiTrash2}
        visible={confirmationDialogVisible}
        setVisible={setConfirmationDialogVisible}
        isLoading={delLoading}
        onAccept={confirmDeleteHandler}
        acceptLabel={t('delete')}
        rejectLabel={t('cancel')}
      />
      {data?.data?.length > 0 && (
        <CustomPagination
          currentPage={data?.page}
          rowsPerPage={data?.per_page}
          totalRecords={data?.total_pages}
          totalRecord={data?.total_record}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );
}
